// PeakEdu Tier 0 WASM — Mic permission denied UX (G1 V7.1 Beta launch SD70).
//
// Vấn đề: getUserMedia() throws nhiều DOMException types, default browser
// message tiếng Anh không thân thiện end-user Việt. Code main.js cũ chỉ
// `setStatus('error', e.message)` → user không biết action recovery.
//
// Giải pháp:
// - Map DOMException name/code → Vietnamese title + action message + recovery link
// - Browser detection (Chrome/Firefox/Safari/Edge/other) → tailored guide
// - Recovery flow: action label cho retry button + link tới browser settings
//
// Reference DOMException types cho getUserMedia:
//   NotAllowedError    — User từ chối explicitly hoặc hidden permission denied
//   NotFoundError      — Không có thiết bị microphone
//   NotReadableError   — Mic đang bị dùng bởi tab/app khác
//   OverconstrainedError — Constraints (sampleRate/channelCount) không đáp ứng
//   SecurityError      — HTTPS bắt buộc (chỉ xảy ra dev qua HTTP)
//   AbortError         — User cancel hoặc page navigate
//   TypeError          — Constraints invalid
//
// Per-browser re-enable steps (verified 2026-05-08):
//   Chrome:  Padlock icon → Site settings → Microphone → Allow
//   Firefox: Padlock icon → Permissions → "Use the Microphone" → Remove permission → Reload
//   Safari:  System Settings → Privacy → Microphone → Allow Safari
//   Edge:    Padlock icon → Permissions for this site → Microphone → Allow

/**
 * Detect browser family from navigator.userAgent.
 * Returns one of: 'chrome' | 'firefox' | 'safari' | 'edge' | 'other'
 */
export function detectBrowser() {
  const ua = (navigator.userAgent || '').toLowerCase();
  if (ua.includes('edg/')) return 'edge';
  if (ua.includes('chrome/') && !ua.includes('chromium/')) return 'chrome';
  if (ua.includes('firefox/')) return 'firefox';
  if (ua.includes('safari/') && !ua.includes('chrome/')) return 'safari';
  return 'other';
}

/**
 * Browser-specific recovery guide (HTML safe, Vietnamese).
 */
export function getBrowserGuide(browser) {
  const guides = {
    chrome: 'Bấm vào biểu tượng 🔒 ổ khóa cạnh thanh địa chỉ → "Cài đặt trang web" → "Micrô" → chọn "Cho phép" → tải lại trang.',
    edge:   'Bấm vào biểu tượng 🔒 ổ khóa cạnh thanh địa chỉ → "Quyền cho trang này" → "Micrô" → chọn "Cho phép" → tải lại trang.',
    firefox: 'Bấm vào biểu tượng 🔒 ổ khóa cạnh thanh địa chỉ → "Permissions" → bỏ chặn "Use the Microphone" → tải lại trang.',
    safari: 'Mở "System Settings" → "Privacy & Security" → "Microphone" → bật quyền cho Safari → tải lại trang.',
    other: 'Mở cài đặt quyền của trình duyệt → cho phép truy cập micrô cho trang này → tải lại trang.',
  };
  return guides[browser] || guides.other;
}

/**
 * Format microphone error → Vietnamese {title, message, recoveryGuide, retryable, errorCode}.
 *
 * @param {Error|DOMException} err — error thrown by getUserMedia()
 * @returns {{
 *   title: string,
 *   message: string,
 *   recoveryGuide: string,
 *   retryable: boolean,
 *   errorCode: string,
 * }}
 */
export function formatMicError(err) {
  const browser = detectBrowser();
  const browserGuide = getBrowserGuide(browser);
  const name = err?.name || 'UnknownError';
  const code = err?.code || 0;

  // Default fallback
  let title = '🎤 Lỗi truy cập micrô';
  let message = err?.message || 'Lỗi không xác định khi truy cập micrô.';
  let recoveryGuide = browserGuide;
  let retryable = true;
  let errorCode = name;

  switch (name) {
    case 'NotAllowedError':
      title = '🚫 Bạn chưa cho phép truy cập micrô';
      message = 'Tính năng chấm điểm phát âm cần quyền dùng micrô để ghi âm giọng đọc của bạn. Hệ thống KHÔNG lưu audio sau khi chấm điểm xong.';
      recoveryGuide = browserGuide;
      retryable = true;
      errorCode = 'mic_permission_denied';
      break;

    case 'NotFoundError':
    case 'DevicesNotFoundError':
      title = '🔌 Không tìm thấy micrô';
      message = 'Trình duyệt không phát hiện được thiết bị micrô nào. Vui lòng kiểm tra: micrô đã cắm vào máy? Cài đặt audio của hệ điều hành đã chọn đúng thiết bị?';
      recoveryGuide = 'Cắm tai nghe có micrô hoặc bật micrô tích hợp của thiết bị. Sau đó bấm "Thử lại".';
      retryable = true;
      errorCode = 'mic_device_not_found';
      break;

    case 'NotReadableError':
    case 'TrackStartError':
      title = '🔒 Micrô đang bị ứng dụng khác sử dụng';
      message = 'Có ứng dụng hoặc tab khác (Zoom/Meet/Discord/...) đang chiếm quyền micrô. Vui lòng đóng ứng dụng đó và thử lại.';
      recoveryGuide = 'Đóng các tab/ứng dụng đang dùng micrô (Zoom, Meet, Discord, browser tabs khác) → bấm "Thử lại".';
      retryable = true;
      errorCode = 'mic_in_use';
      break;

    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      title = '⚙️ Micrô không hỗ trợ chất lượng yêu cầu';
      message = 'Thiết bị micrô của bạn không hỗ trợ thông số ghi âm cần thiết (16kHz mono). Vui lòng thử với micrô khác.';
      recoveryGuide = 'Dùng micrô khác (vd tai nghe có micrô) → bấm "Thử lại".';
      retryable = true;
      errorCode = 'mic_constraints_unmet';
      break;

    case 'SecurityError':
      title = '🔐 Trang chưa dùng kết nối an toàn (HTTPS)';
      message = 'Trình duyệt yêu cầu trang phải chạy trên HTTPS để truy cập micrô. Vui lòng truy cập qua https:// thay vì http://.';
      recoveryGuide = 'Mở lại trang qua URL bắt đầu bằng https:// (ví dụ https://app.peakedu.vn).';
      retryable = false;
      errorCode = 'mic_insecure_context';
      break;

    case 'AbortError':
      title = '⏸ Đã hủy yêu cầu micrô';
      message = 'Yêu cầu cấp quyền micrô đã bị hủy. Vui lòng bấm "Bắt đầu ghi âm" lại.';
      recoveryGuide = 'Bấm nút "Bắt đầu ghi âm" để thử lại.';
      retryable = true;
      errorCode = 'mic_request_aborted';
      break;

    case 'TypeError':
      title = '⚠️ Cấu hình ghi âm không hợp lệ';
      message = 'Lỗi nội bộ — cấu hình audio không hợp lệ. Vui lòng tải lại trang.';
      recoveryGuide = 'Tải lại trang (F5 hoặc Ctrl+R) → thử lại.';
      retryable = true;
      errorCode = 'mic_invalid_config';
      break;

    default:
      title = '🎤 Lỗi truy cập micrô';
      message = `Lỗi không xác định khi truy cập micrô (${name}: ${err?.message || ''}).`;
      recoveryGuide = browserGuide;
      retryable = true;
      errorCode = `mic_unknown_${name}`;
      break;
  }

  return { title, message, recoveryGuide, retryable, errorCode };
}

/**
 * Render mic error vào DOM element (status div).
 * Outputs HTML structure: title + description + recovery guide + retry hint.
 *
 * @param {HTMLElement} statusEl — target status display element
 * @param {{title, message, recoveryGuide, retryable, errorCode}} formatted — output of formatMicError()
 */
export function renderMicErrorToStatus(statusEl, formatted) {
  if (!statusEl) return;

  const { title, message, recoveryGuide, retryable } = formatted;

  // Use innerHTML carefully — content all from constants/sanitized error name
  // err.message MAY contain user-controlled (very unlikely, but escape just in case)
  const escape = (s) => String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));

  const retryHint = retryable
    ? '<div class="mic-error__retry">↻ Sau khi sửa xong, bấm <strong>"Bắt đầu ghi âm"</strong> để thử lại.</div>'
    : '';

  statusEl.className = 'status error';
  statusEl.innerHTML = `
    <div class="mic-error">
      <div class="mic-error__title"><strong>${escape(title)}</strong></div>
      <div class="mic-error__message">${escape(message)}</div>
      <div class="mic-error__guide"><em>Cách khắc phục:</em> ${escape(recoveryGuide)}</div>
      ${retryHint}
    </div>
  `;
}
